import {useState} from 'react';
import {Button} from '@chakra-ui/react';
import {toaster} from '../components/ui/toaster';
import {DriveFile} from '../drive/types';
import {describeDriveError} from '../drive/driveApi';
import {useMoveFiles} from '../drive/queries';
import {PlazmaStars} from './usePlazmaStars';

/** How long the archive toast — and with it the undo button — stays up. Hovering it pauses the timer. */
const UNDO_TOAST_DURATION = 8000;

// Only the latest archive can be undone: a new archive closes the previous toast. Module-level so
// the grid and the viewer share it.
let lastUndoToastId: string | undefined;

export interface ArchiveRequest {
    /** Web photos to archive. */
    files: DriveFile[];
    webFolderId: string;
    archiveFolderId: string;
    /** The web bin's plazma state: needed to remove plazma copies, and to restore them on undo. */
    plazma?: PlazmaStars;
    alsoRemovePlazmaCopies: boolean;
}

function UndoButton({onUndo}: { onUndo: () => Promise<void> }) {
    const [isUndoing, setIsUndoing] = useState(false);
    return <Button
        size="sm"
        mt={2}
        variant="outline"
        color="inherit"
        borderColor="currentColor"
        _hover={{bg: 'whiteAlpha.300'}}
        loading={isUndoing}
        onClick={() => {
            setIsUndoing(true);
            onUndo();
        }}
    >
        Visszavonás
    </Button>;
}

/**
 * Moves web photos to the archive without a confirmation (unless the caller asked about plazma
 * copies first) and shows a success toast whose "Visszavonás" button moves them back — restoring
 * any plazma copies the archive removed — so the fast workflow stays safe.
 */
export function useArchiveWithUndo() {
    const moveFiles = useMoveFiles();
    const [isArchiving, setIsArchiving] = useState(false);

    /** Resolves to whether the files were archived; failures are reported with a toast. */
    async function archive(request: ArchiveRequest): Promise<boolean> {
        const {files, webFolderId, archiveFolderId, plazma} = request;
        const fileIds = files.map((f) => f.id);
        // Resolve the copies before moving, while the photos are still in the web listing.
        const filesLosingCopies = request.alsoRemovePlazmaCopies && plazma ? files.filter(plazma.isStarred) : [];
        const plazmaCopies = plazma ? plazma.copiesOf(filesLosingCopies) : [];

        setIsArchiving(true);
        try {
            await moveFiles.mutateAsync({fileIds, addParentId: archiveFolderId, removeParentId: webFolderId});
            await plazma?.removeCopies(plazmaCopies);
        } catch (e) {
            toaster.create({type: 'error', title: describeDriveError(e)});
            return false;
        } finally {
            setIsArchiving(false);
        }

        async function undo() {
            try {
                await moveFiles.mutateAsync({fileIds, addParentId: webFolderId, removeParentId: archiveFolderId});
                await plazma?.restoreCopies(filesLosingCopies);
                toaster.create({type: 'info', title: 'Visszavonva', duration: 1500});
            } catch (e) {
                toaster.create({type: 'error', title: describeDriveError(e)});
            } finally {
                toaster.dismiss(toastId);
                if (lastUndoToastId === toastId) lastUndoToastId = undefined;
            }
        }

        if (lastUndoToastId !== undefined) toaster.dismiss(lastUndoToastId);
        const toastId = toaster.create({
            type: 'success',
            title: files.length > 1 ? `${files.length} fájl áthelyezve az archívumba` : 'Áthelyezve az archívumba',
            description: <UndoButton onUndo={undo}/>,
            /*action: {
                label: 'Visszavonás',
                onClick: () => undo()
            },*/
            duration: UNDO_TOAST_DURATION,
            closable: true,
        });
        lastUndoToastId = toastId;
        return true;
    }

    return {archive, isArchiving};
}
